import React, { useState, useEffect } from 'react';
import {
  Button,
  TextInput,
  StatusBanner,
  ScreenHeader,
  Spinner,
  SuccessTick,
  LinkButton,
} from './ui';
import { IconBolt, IconAlert, IconShield } from './icons';

type Step = 'INPUT' | 'OTP' | 'LOADING' | 'SUCCESS' | 'ERROR';

interface CDSLFetchModeProps {
  prefill?: { pan?: string; boId?: string; dob?: string };
  onRequestOtp: (pan: string, boId: string, dob: string) => Promise<string>;
  onVerifyOtp: (sessionId: string, otp: string, pan: string) => Promise<void>;
  onBack: () => void;
}

const OTP_LENGTH = 4;

export const CDSLFetchMode: React.FC<CDSLFetchModeProps> = ({
  prefill,
  onRequestOtp,
  onVerifyOtp,
  onBack,
}) => {
  const [step, setStep] = useState<Step>('INPUT');
  const [pan, setPan] = useState(prefill?.pan || '');
  const [boId, setBoId] = useState(prefill?.boId || '');
  const [dob, setDob] = useState(prefill?.dob || '');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');
  const [invalidField, setInvalidField] = useState<
    'pan' | 'boId' | 'dob' | 'otp' | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (prefill?.pan) setPan(prefill.pan);
    if (prefill?.boId) setBoId(prefill.boId);
    if (prefill?.dob) setDob(prefill.dob);
  }, [prefill]);

  const validatePan = (v: string) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v);
  const validateBoId = (v: string) => /^\d{16}$/.test(v);
  const validateDob = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInvalidField(null);

    const upperPan = pan.toUpperCase();
    if (!validatePan(upperPan)) {
      setError('That PAN doesn\'t look right — format should be ABCDE1234F.');
      setInvalidField('pan');
      return;
    }
    if (!validateBoId(boId)) {
      setError('BO ID must be exactly 16 digits.');
      setInvalidField('boId');
      return;
    }
    if (!validateDob(dob)) {
      setError('Please enter your date of birth.');
      setInvalidField('dob');
      return;
    }

    setIsLoading(true);
    try {
      const sid = await onRequestOtp(upperPan, boId, dob);
      setSessionId(sid);
      setStep('OTP');
    } catch (err: any) {
      setError(err.message || 'Failed to request OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInvalidField(null);

    if (otp.length !== OTP_LENGTH) {
      setError('Please enter the 4-digit OTP.');
      setInvalidField('otp');
      return;
    }
    setIsLoading(true);
    setStep('LOADING');
    try {
      await onVerifyOtp(sessionId, otp, pan.toUpperCase());
      setStep('SUCCESS');
    } catch (err: any) {
      setError(err.message || 'OTP verification failed.');
      setStep('ERROR');
      setIsLoading(false);
    }
  };

  // ----- Error state -----
  if (step === 'ERROR') {
    return (
      <div style={{ textAlign: 'center', padding: '28px 0' }}>
        <span
          style={{
            display: 'inline-flex',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'var(--pc-color-error-bg)',
            color: 'var(--pc-color-error)',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}
        >
          <IconAlert size={28} />
        </span>
        <h3
          style={{
            margin: '0 0 8px',
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
          }}
        >
          We couldn't fetch your statement
        </h3>
        <p
          style={{
            color: 'var(--pc-color-text-muted)',
            fontSize: 13,
            margin: '0 0 22px',
            lineHeight: 1.5,
          }}
        >
          {error ||
            'Something went wrong while contacting CDSL. The OTP may have expired, or your details may not match.'}
        </p>
        <Button
          variant="primary"
          fullWidth
          onClick={() => {
            setStep('OTP');
            setError('');
            setOtp('');
          }}
          style={{ marginBottom: 10 }}
        >
          Try OTP again
        </Button>
        <Button
          variant="secondary"
          fullWidth
          onClick={() => {
            setStep('INPUT');
            setError('');
            setOtp('');
          }}
        >
          Start over
        </Button>
      </div>
    );
  }

  // ----- Loading state -----
  if (step === 'LOADING') {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '56px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Spinner size={44} />
        <h3
          style={{
            margin: '20px 0 6px',
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
          }}
        >
          Importing your portfolio
        </h3>
        <p
          style={{
            color: 'var(--pc-color-text-muted)',
            fontSize: 13,
            margin: 0,
          }}
        >
          Securely fetching your demat holdings
        </p>
      </div>
    );
  }

  // ----- Success state -----
  if (step === 'SUCCESS') {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <SuccessTick size={56} />
        <h3
          style={{
            margin: '14px 0 6px',
            fontSize: 20,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
          }}
        >
          Portfolio imported
        </h3>
        <p
          style={{ color: 'var(--pc-color-text-muted)', fontSize: 13, margin: 0 }}
        >
          Your demat holdings are now available
        </p>
      </div>
    );
  }

  // ----- OTP step -----
  if (step === 'OTP') {
    return (
      <div>
        <ScreenHeader
          title="Enter the OTP"
          description="CDSL just sent a 4-digit code to the mobile number registered with your demat account."
        />
        <form onSubmit={handleVerifyOtp}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--pc-color-text)',
              marginBottom: 6,
            }}
          >
            4-digit OTP
          </label>
          <TextInput
            value={otp}
            invalid={invalidField === 'otp'}
            inputMode="numeric"
            autoComplete="one-time-code"
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))
            }
            placeholder="••••"
            maxLength={OTP_LENGTH}
            autoFocus
            style={{
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: 8,
              textAlign: 'center',
            }}
          />

          <div
            style={{
              fontSize: 11,
              color: 'var(--pc-color-text-faded)',
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            Didn't get the code? Make sure your mobile number is up-to-date
            with CDSL. You can also{' '}
            <LinkButton
              type="button"
              onClick={() => {
                setOtp('');
                setError('');
                setStep('INPUT');
              }}
            >
              start over
            </LinkButton>{' '}
            to request a fresh OTP.
          </div>

          {error && (
            <div style={{ marginTop: 12 }}>
              <StatusBanner tone="error" icon={<IconAlert size={16} />}>
                {error}
              </StatusBanner>
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <Button
              variant="primary"
              fullWidth
              type="submit"
              loading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Verifying' : 'Verify and import'}
            </Button>
          </div>

          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <LinkButton
              type="button"
              onClick={() => {
                setStep('INPUT');
                setError('');
              }}
            >
              Back to details
            </LinkButton>
          </div>
        </form>
      </div>
    );
  }

  // ----- Input step -----
  return (
    <div>
      <ScreenHeader
        icon={<IconBolt size={22} />}
        title="Fetch via CDSL OTP"
        description="OTP to your CDSL-registered mobile. Returns your full holdings (CDSL + NSDL)."
      />

      <StatusBanner tone="info">
        Works with any <strong>CDSL demat</strong> account.
      </StatusBanner>

      <form onSubmit={handleRequestOtp} style={{ marginTop: 14 }}>
        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>PAN number</label>
          <TextInput
            value={pan}
            invalid={invalidField === 'pan'}
            onChange={(e) =>
              // Strip whitespace from paste; auto-uppercase as the user types.
              setPan(e.target.value.replace(/\s+/g, '').toUpperCase())
            }
            placeholder="ABCDE1234F"
            maxLength={10}
            autoComplete="off"
            style={{ textTransform: 'uppercase' }}
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Demat account number (BO ID)</label>
          <TextInput
            value={boId}
            invalid={invalidField === 'boId'}
            inputMode="numeric"
            onChange={(e) =>
              // /\D/ already strips whitespace alongside non-digits.
              setBoId(e.target.value.replace(/\D/g, '').slice(0, 16))
            }
            placeholder="1234567890123456"
            maxLength={16}
          />
          <div
            style={{
              fontSize: 11,
              color: 'var(--pc-color-text-faded)',
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            <strong>16 digits.</strong> Find it on any CDSL contract note,
            or in your broker app under{' '}
            <em>Profile → Account → Demat / DP details</em>.
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={fieldLabel}>Date of birth</label>
          <TextInput
            type="date"
            value={dob}
            invalid={invalidField === 'dob'}
            onChange={(e) => setDob(e.target.value)}
          />
        </div>

        {error && (
          <div style={{ marginBottom: 14 }}>
            <StatusBanner tone="error" icon={<IconAlert size={16} />}>
              {error}
            </StatusBanner>
          </div>
        )}

        <Button variant="primary" fullWidth type="submit" loading={isLoading}>
          {isLoading ? 'Requesting OTP' : 'Send me an OTP'}
        </Button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginTop: 12,
            fontSize: 11,
            color: 'var(--pc-color-text-faded)',
          }}
        >
          <IconShield size={12} />
          <span>We don't store your PAN, BO ID or date of birth.</span>
        </div>

        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <LinkButton type="button" onClick={onBack}>
            I already have my file
          </LinkButton>
        </div>
      </form>
    </div>
  );
};

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--pc-color-text)',
  marginBottom: 6,
};
